import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { BsPeopleFill } from "react-icons/bs";
import { MdOutlineFolderOpen, MdPostAdd } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTheme } from "styled-components";
import { Button } from "../../../components/common";
import {
  Buttons,
  ContentContainer,
  OnboardingDescription,
  OnboardingTitle,
} from "../../../components/common/onboarding/index.styled";
import AutoCompleteSelect, {
  OptionProps,
} from "../../../components/onboarding/autocomplete-select";
import InputComponent from "../../../components/onboarding/input";
import { updateUserCompany } from "../../../store/company/company-actions";
import OnboardingLayout from "../onboarding/components/onboarding-layout";
import OptionsRadio from "../../../components/common/options-check/options-check";
import { fetchSignUpReasons } from "../../../store/common/common-actions";
import { AppState } from "../../../store/app-state";
import { useTranslation } from "react-i18next";
import { Option as OptionItem } from "../../../components/common/options-check/options-check";

export const OnboardingCompanyPage: React.FC = () => {
  const theme = useTheme();
  const { t: root } = useTranslation("private-home-page", { keyPrefix: "onboarding.company" });
  const [selectedEmployeesRange, setEmployeesRange] = useState<OptionProps>();
  const [selectedSector, setCompanySector] = useState<OptionProps>();
  const [companyName, setCompanyName] = useState<string>("");
  const [reasonOptions, setReasonOptions] = useState<OptionItem[]>([]);
  const dispatch = useDispatch();
  const history = useHistory();
  const { signupReasons } = useSelector((state: AppState) => state.commonState);

  const selectedReason = useMemo((): OptionItem | undefined => {
    return reasonOptions.find(o => o.selected);
  }, [reasonOptions]);

  const ableToSaveNewRegister = useMemo((): boolean => {
    return !!companyName && !!selectedEmployeesRange && !!selectedSector && !!selectedReason;
  }, [companyName, selectedEmployeesRange, selectedSector, selectedReason]);

  useEffect(() => {
    setReasonOptions(signupReasons.data.map((reason) => (
      { label: reason.label, value: reason.id, selected: false }
    )));
  }, [signupReasons])

  const employeesRange = [
    "just-me",
    "2-10",
    "11-50",
    "51-150",
    "151-500",
    "501-1000",
    "more-than-1000",
  ];

  const companySectors = ["rh", "marketing", "sales", "finance", "it", "other"];

  const rangeToOption = useCallback(
    (range: string): OptionProps => {
      return {
        value: range,
        label: root(`employeesRange.${range}`, {
          defaultValue: range,
        }),
      };
    },
    [root]
  );

  const sectorToOption = useCallback(
    (sector: string): OptionProps => {
      return {
        value: sector,
        label: root(`sectors.${sector}`, {
          defaultValue: sector,
        }),
      };
    },
    [root]
  );

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      dispatch(
        updateUserCompany(companyName, selectedEmployeesRange?.value ?? "", selectedReason?.value ?? "")
      );
      history.push('/onboarding');
    },
    [dispatch, companyName, selectedEmployeesRange, history, selectedReason]
  );

  useEffect(() => {
    dispatch(fetchSignUpReasons());
  }, [dispatch]);

  return (
    <OnboardingLayout>
      <ContentContainer>
        <OnboardingTitle color={theme.colors.primaryBlue.main} align="start">
          {root("title")}
        </OnboardingTitle>
        <OnboardingDescription color={theme.colors.gray900.main} margin="0">
          {root("sub-title")}
        </OnboardingDescription>
        <form onSubmit={handleSubmit}>
          <InputComponent
            placeholder={`*${root("fields.name")}`}
            onChange={(e) => {
              setCompanyName(e);
            }}
            value={companyName}
            icon={MdPostAdd}
            color={theme.colors.primaryBlue.main}
          />
          <AutoCompleteSelect
            placeholder={`*${root("fields.sector")}`}
            icon={MdOutlineFolderOpen}
            primaryColor={theme.colors.primaryBlue.main}
            secondaryColor={theme.colors.gray900.main}
            allowWrite={false}
            options={companySectors.map((sector) => sectorToOption(sector))}
            value={sectorToOption(selectedSector?.value ?? "")}
            setValue={setCompanySector}
          />
          <AutoCompleteSelect
            placeholder={`*${root("fields.size")}`}
            icon={BsPeopleFill}
            primaryColor={theme.colors.primaryBlue.main}
            secondaryColor={theme.colors.gray900.main}
            allowWrite={false}
            options={employeesRange.map((range) => rangeToOption(range))}
            value={rangeToOption(selectedEmployeesRange?.value ?? "")}
            setValue={setEmployeesRange}
          />

          <OptionsRadio 
            title={root("sign-up-reason.title")}
            options={reasonOptions}
            setOptions={setReasonOptions}
            color={theme.colors.primaryBlue.main}
            otherOptionLabel={root("sign-up-reason.other")}
          />

          <Buttons>
            <Button
              textColor={theme.colors.primaryBlue.text}
              backgroundColor={ableToSaveNewRegister 
                  ? theme.colors.primaryBlue.main 
                  : theme.colors.gray400.main}
              width="fit-content"
              type="submit"
              disabled={!ableToSaveNewRegister}
            >
              {root("button")}
            </Button>
          </Buttons>
        </form>
      </ContentContainer>
    </OnboardingLayout>
  );
};
